import { ScoringBand } from '@@types/api.types';
import { type BadgeProps } from '@components/ui/badge';
import type { MeiaTaskReadinessStatus } from './meia-task-column.util';

export type ScoringBandTone = {
  badgeVariant: BadgeProps['variant'];
  bgClass: string;
  borderClass: string;
  dotClass: string;
  textClass: string;
};

const FALLBACK_TONE: ScoringBandTone = {
  badgeVariant: 'pending',
  bgClass: 'bg-gray-50',
  borderClass: 'border-gray-200',
  dotClass: 'bg-gray-300',
  textClass: 'text-gray-500',
};

const BAND_TONES: Readonly<Record<ScoringBand, ScoringBandTone>> = {
  [ScoringBand.GREEN]: {
    badgeVariant: 'success',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    dotClass: 'bg-green-600',
    textClass: 'text-green-700',
  },
  [ScoringBand.PENDING]: {
    badgeVariant: 'pending',
    bgClass: 'bg-gray-50',
    borderClass: 'border-gray-200',
    dotClass: 'bg-gray-300',
    textClass: 'text-gray-500',
  },
  [ScoringBand.RED]: {
    badgeVariant: 'destructive',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    dotClass: 'bg-red-600',
    textClass: 'text-red-700',
  },
  [ScoringBand.YELLOW]: {
    badgeVariant: 'warning',
    bgClass: 'bg-yellow-50',
    borderClass: 'border-yellow-200',
    dotClass: 'bg-yellow-500',
    textClass: 'text-yellow-700',
  },
};

const BAND_LABELS: Readonly<Record<ScoringBand, string>> = {
  [ScoringBand.GREEN]: 'Green',
  [ScoringBand.PENDING]: 'Pending',
  [ScoringBand.RED]: 'Red',
  [ScoringBand.YELLOW]: 'Yellow',
};

export const getScoringBandTone = (band?: ScoringBand | null): ScoringBandTone =>
  band ? BAND_TONES[band] : FALLBACK_TONE;

export const getScoringBandLabel = (band?: ScoringBand | null): string => (band ? BAND_LABELS[band] : 'Pending');

/**
 * Maps a scored verdict band onto the shared red/yellow/green readiness icon statuses so
 * P4 cards can reuse `MeiaTaskReadinessStatusIcon`. Pending/unscored bands return `null` —
 * they have no equivalent readiness state and need a neutral visual instead.
 */
export const getScoringBandReadinessStatus = (band?: ScoringBand | null): MeiaTaskReadinessStatus | null => {
  switch (band) {
    case ScoringBand.GREEN:
      return 'complete';
    case ScoringBand.YELLOW:
      return 'partial';
    case ScoringBand.RED:
      return 'empty';
    default:
      return null;
  }
};
